import { useEffect, useState, useRef, useCallback } from 'react'
import { api } from '../api/client.js'
import '../styles/splash-screen.css'

export default function SplashScreen({ onComplete }) {
  const [progress, setProgress] = useState(0)
  const [fading, setFading] = useState(false)
  const [statusText, setStatusText] = useState('Подключение к серверу...')
  const [error, setError] = useState(null)
  const [retrying, setRetrying] = useState(false)

  const animFrameRef = useRef(null)
  const targetRef = useRef(10)
  const currentRef = useRef(0)
  const errorRef = useRef(null)

  // Запуск реальной последовательности загрузки с сервера
  const startRealLoading = useCallback(async () => {
    setError(null)
    errorRef.current = null
    setRetrying(false)
    setStatusText('Подключение к серверу...')
    targetRef.current = 20

    try {
      // 1. Реальная проверка доступности бэкенда
      const health = await api.health()
      if (!health || !health.ok) {
        throw new Error('Сервер временно недоступен')
      }
      targetRef.current = 45
      setStatusText('Загрузка кейсов и скинов...')

      // Пауза для естественного восприятия и плавной отрисовки
      await new Promise((r) => setTimeout(r, 220))

      // 2. Реальная загрузка каталога предметов и кейсов
      const [catalogData, casesData] = await Promise.all([
        api.catalog(),
        api.cases(),
      ])

      if (!catalogData || !casesData) {
        throw new Error('Не удалось загрузить данные кейсов')
      }
      targetRef.current = 78
      setStatusText('Синхронизация ленты дропов...')

      await new Promise((r) => setTimeout(r, 220))

      // 3. Реальная загрузка ленты последних дропов
      await api.liveDrops()

      targetRef.current = 100
      setStatusText('Добро пожаловать!')
    } catch (err) {
      console.error('Ошибка инициализации CaseMania:', err)
      const errorMsg = 'Не удалось связаться с сервером. Проверьте интернет-соединение или повторите попытку.'
      errorRef.current = errorMsg
      setError(errorMsg)
      setStatusText('Ошибка подключения')
    }
  }, [])

  // Плавный 60-144 FPS цикл интерполяции прогресса
  useEffect(() => {
    let alive = true

    const tick = () => {
      if (!alive) return

      if (!errorRef.current) {
        const target = targetRef.current
        const diff = target - currentRef.current

        if (diff > 0.05) {
          // Плавное приближение к целевому прогрессу
          currentRef.current += Math.max(diff * 0.09, 0.35)
          if (currentRef.current > target) currentRef.current = target
        }

        const displayPct = Math.min(Math.round(currentRef.current), 100)
        setProgress(displayPct)

        // Завершение при достижении 100%
        if (currentRef.current >= 100 && target >= 100) {
          setTimeout(() => {
            if (!alive) return
            setFading(true)
            setTimeout(() => {
              if (!alive) return
              onComplete?.()
            }, 500)
          }, 320)
          return
        }
      }

      animFrameRef.current = requestAnimationFrame(tick)
    }

    animFrameRef.current = requestAnimationFrame(tick)
    startRealLoading()

    return () => {
      alive = false
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)
    }
  }, [onComplete, startRealLoading])

  const handleRetry = () => {
    setRetrying(true)
    currentRef.current = 10
    setProgress(10)
    startRealLoading()
  }

  return (
    <div
      className={`splash-overlay ${fading ? 'is-fading' : ''} ${error ? 'is-error' : ''}`}
      aria-hidden="true"
    >
      <div className="splash-backdrop-glow" />

      <div className="splash-card">
        {/* Крупный логотип CaseMania */}
        <div className="splash-logo-wrap">
          <div className="splash-logo-aura" />
          <img src="/cmlogo.png" alt="CaseMania" className="splash-logo-img" />
        </div>

        {!error ? (
          <>
            {/* Реальный плавный прогресс-бар */}
            <div className="splash-progress-track">
              <div
                className="splash-progress-bar"
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Статус загрузки и процент */}
            <div className="splash-status-row">
              <span key={statusText} className="splash-subtitle">
                {statusText}
              </span>
              <span className="splash-percent">{progress}%</span>
            </div>
          </>
        ) : (
          /* Блок ошибки подключения — сайт не открывается, пока нет сервера */
          <div className="splash-error-box">
            <div className="splash-error-badge">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>Ошибка подключения к серверу</span>
            </div>

            <button
              type="button"
              className="btn-splash-retry"
              onClick={handleRetry}
              disabled={retrying}
            >
              <svg
                className={retrying ? 'is-spinning' : ''}
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21.5 2v6h-6" />
                <path d="M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
              </svg>
              <span>{retrying ? 'Подключение...' : 'Повторить попытку'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
